import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import {
  ExpandedMenuEntry,
  LinkEntry,
  PageListAcceptedLinks,
  PageListEntry,
} from "frontend/contentful/schema/primitives";

import { MediaAssetProps } from "design-system/components/primitives/media-asset/media-asset";
import {
  ExpandedLocalNavigationItemProps,
  ExpandedNavigationLinkWithSubItems,
  ExpandedNavigationSubMenuProps,
} from "design-system/components/universal/expanded-local-navigation/local/expanded-local-navigation";
import { NavigationLink } from "design-system/components/universal/local-navigation/local-navigation";
import { filterUndefined } from "design-system/utils/filter-undefined";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import {
  ArchivePageEntry,
  ArticlePageEntry,
  ClubPageEntry,
  CoursePageEntry,
  DatabasePageEntry,
  DatasetPageEntry,
  DetailPageEntry,
  KnowledgeBaseArticlePageEntry,
  LandingPageEntry,
  ParticipantStoryEntry,
  PodcastEpisodePageEntry,
  ProfilePageEntry,
  ProgramPageEntry,
} from "frontend/contentful/schema/pages";
import {
  GetEntry,
  GetEntryUrl,
  useContentful,
} from "frontend/hooks/use-contentful";
import { createCtaLinkProps } from "../primitives/cta-link.props";
import { createLinkProps } from "../primitives/link.props";
import { createMediaAssetProps } from "../primitives/media-asset.props";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";

type FeatureLinks = NavigationLink & { mediaAsset?: MediaAssetProps };

type PageListAcceptedEntries =
  | LinkEntry
  | ArchivePageEntry
  | ArticlePageEntry
  | ClubPageEntry
  | CoursePageEntry
  | DatabasePageEntry
  | DatasetPageEntry
  | DetailPageEntry
  | KnowledgeBaseArticlePageEntry
  | LandingPageEntry
  | ParticipantStoryEntry
  | PodcastEpisodePageEntry
  | ProfilePageEntry
  | ProgramPageEntry;

const getPageValues = (
  item: PageListAcceptedEntries,
  getEntryUrl: GetEntryUrl,
  getEntry: GetEntry,
): FeatureLinks => {
  if (isContentType<LinkEntry>("link", item)) {
    return {
      title: item.fields.text || "",
      url: getEntryUrl(item),
    };
  }

  if (isContentType<CoursePageEntry>("coursePage", item)) {
    const { title, description: descDoc } = item.fields;

    const description = descDoc ? (
      <RenderContentfulRichText document={descDoc} />
    ) : null;

    return {
      title: title || "",
      description,
      url: getEntryUrl(item),
    };
  }

  if (isContentType<ArticlePageEntry>("articlePage", item)) {
    const { title, description, shortDescription, teaseImage, leadImage } =
      item.fields;

    const mediaAsset = leadImage
      ? createMediaAssetProps(leadImage)
      : createMediaAssetProps(teaseImage);

    const descriptionText = shortDescription
      ? documentToString(shortDescription)
      : documentToString(description);

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
      mediaAsset,
    };
  }

  if (
    isContentType<DatabasePageEntry>("databasePage", item) ||
    isContentType<DatasetPageEntry>("datasetPage", item)
  ) {
    const { title, description, shortDescription } = item.fields;

    const descriptionText = shortDescription
      ? documentToString(shortDescription)
      : documentToString(description);

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
    };
  }

  if (isContentType<PodcastEpisodePageEntry>("podcastEpisodePage", item)) {
    const podcast = getEntry(item.fields.podcastEpisode);
    const { title, description, subtitle, relatedImage } =
      podcast?.fields || {};
    const descriptionText = documentToString(description) || subtitle;

    const mediaAsset = createMediaAssetProps(relatedImage);

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
      mediaAsset,
    };
  }

  if (isContentType<ProgramPageEntry>("programPage", item)) {
    const { title, description, media } = item.fields;
    const descriptionText = documentToString(description);

    const mediaAsset = createMediaAssetProps(media);

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
      mediaAsset,
    };
  }

  if (
    isContentType<KnowledgeBaseArticlePageEntry>(
      "knowledgeBaseArticlePage",
      item,
    )
  ) {
    const { title, subtitle, shortDescription } = item.fields;
    const descriptionText = documentToString(subtitle) || shortDescription;

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
    };
  }

  if (isContentType<ParticipantStoryEntry>("participantStory", item)) {
    const { title, subTitle, shortDescription, image } = item.fields;
    const descriptionText = subTitle || documentToString(shortDescription);

    const mediaAsset = createMediaAssetProps(image);

    return {
      title: title || "",
      description: descriptionText,
      url: getEntryUrl(item),
      mediaAsset,
    };
  }

  if (isContentType<ClubPageEntry>("clubPage", item)) {
    const { title, image } = item.fields;

    const mediaAsset = createMediaAssetProps(image);

    return {
      title: title || "",
      description: undefined,
      url: getEntryUrl(item),
      mediaAsset,
    };
  }

  if (isContentType<LandingPageEntry>("landingPage", item)) {
    const { description, title } = item.fields;

    return {
      title: title || "",
      description,
      url: getEntryUrl(item),
    };
  }

  /*
  The rest. So far:
  - ArchivePageEntry
  - DetailPageEntry
  - ProfilePageEntry
  */
  return {
    title: item.fields.title || "",
    description: undefined,
    url: getEntryUrl(item),
  };
};

export const createExpandedMenuProps = createContentfulProps<
  ExpandedMenuEntry,
  ExpandedLocalNavigationItemProps
>(({ entry: cfEntry }) => {
  const { getEntry, getEntryUrl } = useContentful();

  const mapToNavigationLink = (
    items: Array<PageListAcceptedLinks> | undefined,
  ): Array<FeatureLinks> => {
    return (
      items
        ?.map((itemsEntry) => {
          const item = getEntry(itemsEntry);
          if (!item?.fields) return undefined;
          const pageValues = getPageValues(item, getEntryUrl, getEntry);
          return pageValues;
        })
        .filter(filterUndefined) || []
    );
  };

  const transform = (expandedMenuEntry: ExpandedMenuEntry) => {
    const props: ExpandedLocalNavigationItemProps = {
      title: expandedMenuEntry.fields.title || "",
      layoutType: "vertical",
      pageGroups: [],
    };

    if (expandedMenuEntry.fields.singleTopLink) {
      const singleTopLink = getEntry(expandedMenuEntry.fields.singleTopLink);
      props.singleTopLink = createLinkProps(singleTopLink);
    }

    if (expandedMenuEntry.fields.cta) {
      const cta = getEntry(expandedMenuEntry.fields.cta);
      props.cta = createCtaLinkProps(cta);
    }

    const { menu: pageGroupEntries } = expandedMenuEntry.fields;

    if (!pageGroupEntries?.length) {
      return;
    }

    if (pageGroupEntries.length > 1) {
      props.layoutType = "horizontal";
    }

    const pageGroups = pageGroupEntries
      .map((pageGroupEntry) => {
        const pageGroup = getEntry(pageGroupEntry);
        if (!pageGroup) return undefined;

        const { menu: pageListEntries, title } = pageGroup.fields;

        if (!pageListEntries) return undefined;

        const pageListOrLinks = pageListEntries
          .map((pageListEntry) => {
            const pageList = getEntry(pageListEntry);
            if (!pageList) return undefined;

            if (isContentType<PageListEntry>("pageList", pageList)) {
              const {
                title,
                featureCard,
                featureDescription,
                featureLinks,
                featureLinksTitle,
                featureMedia,
                menu,
                topLinks,
              } = pageList.fields;

              const result: ExpandedNavigationLinkWithSubItems = {
                title,
                featureCard,
                featureDescription,
                featureLinksTitle,
                featureMedia,
                featureLinks: mapToNavigationLink(featureLinks),
                menu: mapToNavigationLink(menu),
                topLinks: mapToNavigationLink(topLinks),
              };

              return result;
            } else {
              // Not a pageList, but a Link.
              const link = pageList;
              const item = getEntry(link);
              if (!item?.fields) return undefined;
              return getPageValues(item, getEntryUrl, getEntry);
            }
          })
          .filter(filterUndefined);

        const result: ExpandedNavigationSubMenuProps = {
          title,
          pageListOrLinks,
        };

        return result;
      })
      .filter(filterUndefined);

    props.pageGroups = pageGroups;

    return props;
  };

  return transform(cfEntry);
});
